/**
 * Admin › In Memoriam › CWGC + FamilySearch bulk-import panel.
 *
 * Two subpanels, one for each source. Both accept a CSV file drop or paste-JSON
 * array. Column names are flexible (see backend cwgc_source.CSV_ALIASES).
 */
import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Upload, FileText, ChevronDown, ChevronUp } from "lucide-react";

const CWGC_TEMPLATE = `Given Name,Surname,Rank,Regiment,Service Number,Conflict,Date of Death,Age at Death,Cemetery,Grave Reference,Country,Casualty URL,Nationality
John,Kipling,Second Lieutenant,Irish Guards,,WWI,1915-09-27,18,Loos Memorial,Panel 4 to 6,France,https://www.cwgc.org/find-records/find-war-dead/casualty/1621340/kipling,-john/,British`;

const FS_TEMPLATE = `Given Name,Surname,Date of Birth,Date of Death,Birth Place,Death Place,Sex,Occupation,FamilySearch ID,URL,Source Dataset,Notes
Sarah,Winchester,1839-01-01,1922-09-05,"New Haven, Connecticut","San Jose, California",female,Heiress,KWCX-JS9,https://www.familysearch.org/tree/person/details/KWCX-JS9,US Historical Records,Heiress of the Winchester rifle fortune.`;

export default function AdminInMemoriamImports({ status, onReload }) {
  return (
    <div className="mt-12 space-y-8" data-testid="admin-in-memoriam-imports">
      <div>
        <div className="overline mb-2">IN MEMORIAM · BULK IMPORT</div>
        <h3 className="font-display text-2xl">Import from CWGC & FamilySearch.</h3>
        <p className="text-xs opacity-70 max-w-2xl mt-1">
          Upload public-domain records supplied by CWGC (Commonwealth War Graves,
          Crown Copyright / OGL v3.0) or FamilySearch (CC0 datasets). All imported
          records store canonical source URLs for attribution.
        </p>
      </div>
      <ImportBlock
        source="cwgc" title="Commonwealth War Graves Commission"
        endpoint="/admin/in-memoriam/cwgc"
        csvTemplate={CWGC_TEMPLATE}
        status={status?.cwgc}
        onReload={onReload}
        blurb="War-dead records are authoritative — they auto-publish on import. Every record links back to its canonical CWGC casualty page."
      />
      <ImportBlock
        source="familysearch" title="FamilySearch (public-domain)"
        endpoint="/admin/in-memoriam/familysearch"
        csvTemplate={FS_TEMPLATE}
        status={status?.familysearch}
        onReload={onReload}
        blurb="Public-domain records land in the moderation queue so families can claim or opt-out before publication. Attribution links back to familysearch.org."
      />
    </div>
  );
}

function ImportBlock({ source, title, endpoint, csvTemplate, status, blurb, onReload }) {
  const [open, setOpen] = useState(false);
  const [csv, setCsv] = useState("");
  const [json, setJson] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [mode, setMode] = useState("csv"); // csv | json

  const submitFile = async () => {
    if (!file) return toast.error("Choose a CSV file first");
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await http.post(`${endpoint}/import-csv`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`${source.toUpperCase()}: +${data.inserted} · ↺${data.updated} · skip ${data.skipped}`);
      onReload?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  const submitCsvText = async () => {
    if (!csv.trim()) return toast.error("Paste CSV rows first");
    setBusy(true); setResult(null);
    try {
      const blob = new Blob([csv], { type: "text/csv" });
      const fd = new FormData();
      fd.append("file", blob, `${source}.csv`);
      const { data } = await http.post(`${endpoint}/import-csv`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`+${data.inserted} inserted · ↺${data.updated} updated`);
      onReload?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  const submitJson = async () => {
    let arr;
    try {
      arr = JSON.parse(json);
      if (!Array.isArray(arr)) throw new Error("Top-level must be an array");
    } catch (e) {
      return toast.error("Invalid JSON: " + e.message);
    }
    setBusy(true); setResult(null);
    try {
      const { data } = await http.post(`${endpoint}/import`, { records: arr });
      setResult(data);
      toast.success(`+${data.inserted} inserted · ↺${data.updated} updated`);
      onReload?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="wofy-card" data-testid={`import-${source}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-4">
        <div className="text-left">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4" style={{ color: "var(--wofy-primary)" }} />
            <h4 className="font-display text-xl">{title}</h4>
            {status?.enabled && (
              <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
                {status.records ?? 0} imported
              </span>
            )}
          </div>
          <p className="text-xs opacity-70 mt-1">{blurb}</p>
        </div>
        {open ? <ChevronUp className="w-4 h-4 shrink-0" /> : <ChevronDown className="w-4 h-4 shrink-0" />}
      </button>

      {open && (
        <div className="mt-5 space-y-4">
          <div className="flex gap-1.5">
            {["csv", "json"].map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className={`px-3 py-1.5 text-xs rounded-full ${mode === m ? "" : "hover:bg-[color:var(--wofy-surface)]"}`}
                style={mode === m ? { background: "var(--wofy-primary)", color: "#FDFBF7" } : { border: "1px solid var(--wofy-border)" }}
              >
                {m.toUpperCase()}
              </button>
            ))}
          </div>

          {mode === "csv" && (
            <>
              <label className="block">
                <span className="text-xs font-mono uppercase opacity-70">Upload CSV file</span>
                <input
                  type="file"
                  accept=".csv,.tsv,.txt"
                  data-testid={`import-file-${source}`}
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="mt-1 block text-sm"
                />
              </label>
              <button
                onClick={submitFile}
                disabled={busy || !file}
                data-testid={`import-submit-file-${source}`}
                className="wofy-btn-primary text-sm"
              >
                <Upload className="w-4 h-4" /> Import file
              </button>
              <details className="text-xs opacity-80">
                <summary className="cursor-pointer">Or paste CSV rows directly</summary>
                <textarea
                  value={csv} onChange={(e) => setCsv(e.target.value)}
                  placeholder={csvTemplate}
                  rows={6}
                  data-testid={`import-csv-${source}`}
                  className="wofy-input font-mono text-xs mt-2 w-full"
                />
                <button onClick={submitCsvText} disabled={busy} className="wofy-btn-ghost text-xs mt-2">Import pasted CSV</button>
              </details>
            </>
          )}

          {mode === "json" && (
            <>
              <label className="block">
                <span className="text-xs font-mono uppercase opacity-70">Paste JSON array of records</span>
                <textarea
                  value={json} onChange={(e) => setJson(e.target.value)}
                  placeholder='[{"name":"...", "died":"1918-11-04", ...}]'
                  rows={8}
                  data-testid={`import-json-${source}`}
                  className="wofy-input font-mono text-xs mt-1 w-full"
                />
              </label>
              <button
                onClick={submitJson}
                disabled={busy || !json.trim()}
                data-testid={`import-submit-json-${source}`}
                className="wofy-btn-primary text-sm"
              >
                <Upload className="w-4 h-4" /> Import JSON
              </button>
            </>
          )}

          {result && (
            <div className="text-xs font-mono border-t pt-3 opacity-90">
              Result: <strong>+{result.inserted}</strong> inserted ·
              ↺{result.updated} updated ·
              skip {result.skipped} · total {result.total}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
